import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { Dialog } from "shared/Dialog/Dialog";
import { DialogContent } from "shared/Dialog/DialogContent";
import { DialogHeader } from "shared/Dialog/DialogHeader";
import { DialogTitle } from "shared/Dialog/DialogTitle";
import { DialogFooter } from "shared/Dialog/DialogFooter";
import Button from "shared/Button";
import FormInput from "shared/FormInput";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { iAdditionalService, AdditionalServiceFormData } from "./PickPackTypes";

interface AdditionalServiceFormDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: AdditionalServiceFormData) => void;
  initialData?: iAdditionalService | null;
  mode: "add" | "edit";
  serviceLevel: "item" | "order";
}

const AdditionalServiceFormDialog: React.FC<
  AdditionalServiceFormDialogProps
> = ({ isOpen, onClose, onSubmit, initialData, mode, serviceLevel }) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const schema = yup.object().shape({
    name: yup
      .string()
      .required(t("additionalServiceFormDialog.validation.nameRequired")),
    description: yup
      .string()
      .required(
        t("additionalServiceFormDialog.validation.descriptionRequired")
      ),
    price: yup
      .number()
      .transform((value, originalValue) =>
        originalValue === "" ? undefined : value
      )
      .required(t("additionalServiceFormDialog.validation.priceRequired"))
      .min(0, t("additionalServiceFormDialog.validation.priceMin")),
    service_level: yup
      .string()
      .oneOf(["item", "order"])
      .required(),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<AdditionalServiceFormData>({
    resolver: yupResolver(schema),
    defaultValues: initialData || {
      name: "",
      description: "",
      price: 0,
      service_level: serviceLevel,
    },
  });

  useEffect(() => {
    if (isOpen) {
      reset(
        initialData || {
          name: "",
          description: "",
          price: 0,
          service_level: serviceLevel,
        }
      );
    }
  }, [initialData, reset, isOpen, serviceLevel]);

  const handleFormSubmit = async (data: AdditionalServiceFormData) => {
    try {
      setIsSubmitting(true);
      // Ensure the service_level is set correctly
      const submitData = { ...data, service_level: serviceLevel };
      await onSubmit(submitData);
      handleClose();
    } catch (error) {
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    reset({
      name: "",
      description: "",
      price: 0,
      service_level: serviceLevel,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex justify-between items-center">
            <span>
              {mode === "add"
                ? serviceLevel === "item"
                  ? t("additionalServiceFormDialog.title.addItemService") ||
                    "Add Item Service"
                  : t("additionalServiceFormDialog.title.addOrderService") ||
                    "Add Order Service"
                : serviceLevel === "item"
                ? t("additionalServiceFormDialog.title.editItemService") ||
                  "Edit Item Service"
                : t("additionalServiceFormDialog.title.editOrderService") ||
                  "Edit Order Service"}
            </span>
            <button
              onClick={handleClose}
              className="rounded-full p-1 hover:bg-gray-100 transition-colors"
            >
              <X size={18} />
            </button>
          </DialogTitle>
        </DialogHeader>

        <div className="p-6 flex flex-col h-full justify-start">
          <div className="grid grid-cols-1 gap-4">
            <FormInput
              label={t("additionalServiceFormDialog.form.name")}
              name="name"
              register={register}
              errors={errors}
              placeholder={t(
                "additionalServiceFormDialog.form.namePlaceholder"
              )}
            />

            <FormInput
              label={t("additionalServiceFormDialog.form.description")}
              name="description"
              register={register}
              errors={errors}
              placeholder={t(
                "additionalServiceFormDialog.form.descriptionPlaceholder"
              )}
            />

            <FormInput
              label={t("additionalServiceFormDialog.form.price")}
              name="price"
              type="number"
              register={register}
              errors={errors}
              placeholder={t(
                "additionalServiceFormDialog.form.pricePlaceholder"
              )}
            />
          </div>
        </div>

        <DialogFooter className="mt-4 sm:mt-6 flex flex-col sm:flex-row gap-2">
          <Button
            type="button"
            variant="outlined"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            {t("additionalServiceFormDialog.buttons.cancel")}
          </Button>
          <Button
            onClick={handleSubmit(handleFormSubmit)}
            isLoading={isSubmitting}
          >
            {mode === "add"
              ? t("additionalServiceFormDialog.buttons.add")
              : t("additionalServiceFormDialog.buttons.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdditionalServiceFormDialog;
